import { useEffect } from "react";

interface PageSEOProps {
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown>;
}

export default function PageSEO({ title, description, path, ogImage, ogType = "website", jsonLd }: PageSEOProps) {
  const siteUrl = typeof import.meta !== "undefined" && import.meta.env ? (import.meta.env.VITE_SITE_URL as string) || "https://acbuyspreadsheet.casa" : "https://acbuyspreadsheet.casa";

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, property: string, content: string) => {
      let el: HTMLMetaElement | null = null;
      if (name) el = document.querySelector(`meta[name="${name}"]`);
      if (!el && property) el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement("meta");
        if (name) el.setAttribute("name", name);
        if (property) el.setAttribute("property", property);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", "", description);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${siteUrl}${path}`);

    setMeta("", "og:title", title);
    setMeta("", "og:description", description);
    setMeta("", "og:url", `${siteUrl}${path}`);
    setMeta("", "og:type", ogType);
    if (ogImage) setMeta("", "og:image", ogImage);

    setMeta("twitter:card", "", "summary_large_image");
    setMeta("twitter:title", "", title);
    setMeta("twitter:description", "", description);
  }, [title, description, path, ogImage, ogType, siteUrl]);

  useEffect(() => {
    const old = document.getElementById("json-ld");
    if (old) old.remove();

    if (!jsonLd) return;

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "json-ld";
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById("json-ld");
      if (s) s.remove();
    };
  }, [jsonLd]);

  return null;
}